import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon as ArrowLeft, FingerprintIcon as Fingerprint, CircleNotchIcon as Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getWorkspace, listImportBatches } from "@/lib/api";
import { ENTITIES } from "@/lib/metadataConstants";
import { BATCH_STATUS_LABEL } from "@/lib/provenanceDisplay";
import ImportBatchTable from "@/components/metadata/ImportBatchTable";
import ImportBatchDetail from "@/components/metadata/ImportBatchDetail";

const PAGE = 50;

/** Evidence / Import History (Iter 38) — every import batch for a workspace. */
export default function WorkspaceImportHistory() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [workspace, setWorkspace] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageNum, setPageNum] = useState(0);
  const sourceCommand = params.get("command") || "";
  const status = params.get("status") || "";
  const entity = params.get("entity") || "";
  const selected = params.get("batch") || "";

  const setParam = useCallback((key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
    if (key !== "batch") setPageNum(0);
  }, [params, setParams]);

  useEffect(() => {
    getWorkspace(id).then((d) => setWorkspace(d?.workspace || null)).catch(() => {});
  }, [id]);

  useEffect(() => {
    setLoading(true);
    listImportBatches(id, { sourceCommand, status, entity, limit: PAGE, offset: pageNum * PAGE })
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load import history."))
      .finally(() => setLoading(false));
  }, [id, sourceCommand, status, entity, pageNum]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <div className="space-y-6" data-testid="import-history-page">
      <div>
        <Link
          to={`/workspaces/${id}/metadata`}
          className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] inline-flex items-center gap-1"
          data-testid="back-to-metadata"
        >
          <ArrowLeft size={11} weight="bold" /> Back to metadata repository
        </Link>
        <h1 className="text-3xl sm:text-4xl font-semibold leading-tight tracking-tight mt-3">
          <Fingerprint size={26} weight="duotone" color="#0f62fe" className="inline-block mr-2 align-text-bottom" />
          Evidence &amp; import history
        </h1>
        <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-2xl">
          Every IBM i evidence row in <strong>{workspace?.name || "this workspace"}</strong> traces back to one
          of these import batches — the uploaded file, its SHA-256, the IBM i command, who imported it and when.
          Legacy batches predate provenance tracking and are labelled as unproven.
        </p>
      </div>

      <div className="ml-card p-4 flex flex-wrap items-center gap-3" data-testid="import-history-filters">
        <div className="w-44">
          <select value={sourceCommand} onChange={(e) => setParam("command", e.target.value)} className="ml-input" data-testid="filter-command">
            <option value="">All commands</option>
            {(data?.source_commands || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="w-52">
          <select value={status} onChange={(e) => setParam("status", e.target.value)} className="ml-input" data-testid="filter-status">
            <option value="">All statuses</option>
            {Object.entries(BATCH_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="w-52">
          <select value={entity} onChange={(e) => setParam("entity", e.target.value)} className="ml-input" data-testid="filter-entity">
            <option value="">All entities</option>
            {ENTITIES.filter((e) => e.importable).map((e) => <option key={e.slug} value={e.slug}>{e.label}</option>)}
          </select>
        </div>
        <span className="ml-auto ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="import-history-total">
          {data ? `${data.total} batch${data.total === 1 ? "" : "es"}` : "—"}
        </span>
      </div>

      <div className={`grid gap-5 ${selected ? "grid-cols-12" : "grid-cols-1"}`}>
        <div className={selected ? "col-span-12 lg:col-span-8 space-y-3" : "space-y-3"}>
          <div className="ml-card overflow-x-auto">
            {loading ? (
              <div className="p-12 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]">
                <Spinner size={18} className="animate-spin mr-2" /> Loading…
              </div>
            ) : (
              <ImportBatchTable items={data?.items || []} selectedId={selected} onSelect={(bid) => setParam("batch", bid)} />
            )}
          </div>
          {data && data.total > PAGE && (
            <div className="flex items-center justify-between text-sm" data-testid="import-history-pagination">
              <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Page {pageNum + 1} of {totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => setPageNum((p) => Math.max(0, p - 1))} disabled={pageNum === 0} className="ml-btn-ghost disabled:opacity-40">← Prev</button>
                <button onClick={() => setPageNum((p) => Math.min(totalPages - 1, p + 1))} disabled={pageNum >= totalPages - 1} className="ml-btn-ghost disabled:opacity-40">Next →</button>
              </div>
            </div>
          )}
        </div>
        {selected && (
          <div className="col-span-12 lg:col-span-4">
            <ImportBatchDetail
              workspaceId={id}
              batchId={selected}
              onClose={() => setParam("batch", "")}
              onJump={(bid) => setParam("batch", bid)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
